import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Input, InputRoot } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import { updatePackage } from '@/services/api/packages';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// The modal only needs identity, the two editable numbers, and a display label.
// A packages-page `PackageRow` satisfies this, and so does a shipment-detail
// item mapped to it — so both callers can reuse the modal.
export interface EditablePackage {
  id: string;
  itemName?: string;
  po?: string;
  weight?: number;
  volume?: number;
  // Current koli. PATCH requires it, so the form prefills from here (falls
  // back to 1 — the BE column default — when the list hasn't sent it yet).
  koli?: number;
}

interface EditPackageModalProps {
  pkg: EditablePackage | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

// Mirrors the backend rule: weight must be positive, volume non-negative, both
// capped at 3 decimals (numeric(10,3)). Koli is a whole number 1..10000 —
// same @IsInt @Min(1) @Max(10000) guard as the BE (and the CSV import ceiling).
const MAX_KOLI = 10000;
const isValidWeight = (n: number) => Number.isFinite(n) && n > 0;
const isValidVolume = (n: number) => Number.isFinite(n) && n >= 0;
const isValidKoli = (n: number) =>
  Number.isInteger(n) && n >= 1 && n <= MAX_KOLI;

const EditPackageModal: React.FC<EditPackageModalProps> = ({
  pkg,
  open,
  onClose,
  onSaved,
}) => {
  const [weight, setWeight] = useState('');
  const [volume, setVolume] = useState('');
  const [koli, setKoli] = useState('');
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const weightRef = useRef<HTMLInputElement>(null);

  // Reset the form whenever a new package is opened for editing. Koli falls
  // back to 1 (the BE column default) so the required PATCH field is always
  // prefilled, even for callers that don't have the value yet.
  useEffect(() => {
    if (open && pkg) {
      setWeight(String(pkg.weight ?? ''));
      setVolume(String(pkg.volume ?? 0));
      setKoli(String(pkg.koli ?? 1));
      setTouched(false);
    }
  }, [open, pkg]);

  const weightNum = Number(weight);
  const volumeNum = Number(volume);
  const koliNum = Number(koli);
  const weightOk = isValidWeight(weightNum);
  const volumeOk = isValidVolume(volumeNum);
  const koliOk = isValidKoli(koliNum);

  const handleSave = async () => {
    setTouched(true);
    if (!pkg || !weightOk || !volumeOk || !koliOk) return;
    setSaving(true);
    try {
      await updatePackage(pkg.id, {
        weight: weightNum,
        volume: volumeNum,
        koli: koliNum,
      });
      showSuccessToast('Paket diperbarui', 'Berat, volume & koli tersimpan.');
      onSaved();
      onClose();
    } catch (err) {
      showErrorToast('Gagal menyimpan', getBackendErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!pkg) return null;

  return (
    <Dialog
      open={open}
      onClose={saving ? () => undefined : onClose}
      title="Edit package"
      description={pkg.itemName || pkg.po}
      closeOnBackdrop={!saving}
      initialFocusRef={weightRef}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex min-h-11 items-center justify-center rounded-md border border-stroke-sub-300 bg-bg-white-0 px-4 text-sm font-medium text-text-sub-600 transition-colors hover:bg-bg-weak-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
            className="min-h-11"
          >
            {saving && <i className="ri-loader-4-line mr-1.5 animate-spin" />}
            Save changes
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        noValidate
      >
        <div className="space-y-1.5">
          <Label htmlFor="edit-weight" required>
            Weight (kg)
          </Label>
          <InputRoot invalid={touched && !weightOk}>
            <Input
              id="edit-weight"
              ref={weightRef}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.001"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="0.000"
            />
          </InputRoot>
          {touched && !weightOk && (
            <p className="text-xs text-error-base">
              Berat harus lebih besar dari 0.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="edit-volume" required>
            Volume (m³)
          </Label>
          <InputRoot invalid={touched && !volumeOk}>
            <Input
              id="edit-volume"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.001"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
              placeholder="0.000"
            />
          </InputRoot>
          {touched && !volumeOk && (
            <p className="text-xs text-error-base">
              Volume tidak boleh negatif.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="edit-koli" required>
            Koli
          </Label>
          <InputRoot invalid={touched && !koliOk}>
            <Input
              id="edit-koli"
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_KOLI}
              step={1}
              value={koli}
              onChange={(e) => setKoli(e.target.value)}
              placeholder="1"
            />
          </InputRoot>
          {touched && !koliOk ? (
            <p className="text-xs text-error-base">
              Koli harus bilangan bulat antara 1 dan {MAX_KOLI}.
            </p>
          ) : (
            <p className="text-xs text-text-soft-400">
              Jumlah dus/paket fisik untuk item ini.
            </p>
          )}
        </div>
      </form>
    </Dialog>
  );
};

export default EditPackageModal;
